import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import 'iconify-icon';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Convert YouTube/Vimeo URL to embed iframe URL.
const toEmbed = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) {
      const id = u.pathname.replace('/', '');
      return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : url;
    }
    if (u.hostname.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      return `https://player.vimeo.com/video/${id}`;
    }
    return url;
  } catch {
    return url;
  }
};

const EneaflixDetail = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(false);
      setShowTrailer(false);
      try {
        // Detail endpoint — returns the FULL high-res `image`.
        // Related items are fetched from the lightweight listing.
        const [detailRes, listRes] = await Promise.all([
          axios.get(`${API_URL}/public/eneaflix/${id}`),
          axios.get(`${API_URL}/public/eneaflix`),
        ]);
        const match = detailRes.data;
        if (!match) {
          setError(true);
        } else {
          setItem(match);
          setRelated(
            (listRes.data || [])
              .filter((x) => x.eneaflix_id !== id && x.enneagram_type === match.enneagram_type)
              .slice(0, 6),
          );
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo({ top: 0 });
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-orange"></div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="bg-black min-h-screen">
        <Header />
        <main className="pt-32 pb-24 min-h-[60vh] text-center max-w-2xl mx-auto px-6">
          <iconify-icon icon="solar:videocamera-linear" className="text-6xl text-neutral-600 mb-4"></iconify-icon>
          <h1 className="text-3xl font-bold mb-3 text-[#EAEAEA]">Título não encontrado</h1>
          <p className="text-neutral-500 mb-8">Este item do Eneaflix pode ter sido removido.</p>
          <Link to="/eneaflix" className="inline-flex items-center gap-2 px-6 py-3 bg-accent-orange text-white rounded-full text-sm font-mono uppercase tracking-wider hover:bg-[#E55D00] transition-colors">
            <iconify-icon icon="solar:arrow-left-linear"></iconify-icon>
            Voltar ao ENEAFLIX
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const embed = toEmbed(item.trailer_url);

  return (
    <div data-testid="eneaflix-detail-page" className="bg-black min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Backdrop hero */}
        <section className="relative bg-black text-white overflow-hidden">
          {item.image && (
            <div className="absolute inset-0 opacity-30">
              <img src={item.image} alt="" className="w-full h-full object-cover blur-xl scale-110" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40"></div>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 relative z-10">
            <Link to="/eneaflix" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-accent-orange transition-colors mb-8">
              <iconify-icon icon="solar:arrow-left-linear"></iconify-icon>
              Voltar ao ENEAFLIX
            </Link>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
              {item.image && (
                <div className="aspect-[2/3] rounded-xl overflow-hidden shadow-2xl shadow-black max-w-xs mx-auto md:mx-0 ring-1 ring-white/10">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="md:col-span-2 space-y-5">
                <div className="flex flex-wrap gap-2 text-xs font-mono uppercase tracking-wider">
                  <span className="px-3 py-1 bg-accent-orange text-white rounded-full">{item.type}</span>
                  <span className="px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-full">{item.enneagram_type}</span>
                </div>
                <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-[#EAEAEA]">{item.title}</h1>
                <p className="text-base md:text-lg text-white/80 leading-[1.8] max-w-2xl">{item.description}</p>
                {embed && (
                  <button
                    onClick={() => setShowTrailer(true)}
                    data-testid="eneaflix-play-trailer-btn"
                    className="inline-flex items-center gap-3 px-7 py-4 bg-accent-orange text-white rounded-full text-sm font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-all transform hover:scale-105 active:scale-95 shadow-lg shadow-accent-orange/30"
                  >
                    <iconify-icon icon="solar:play-bold" className="text-2xl"></iconify-icon>
                    Assistir Trailer
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Trailer Modal */}
        {showTrailer && embed && (
          <div
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setShowTrailer(false)}
            data-testid="eneaflix-trailer-modal"
          >
            <button
              onClick={() => setShowTrailer(false)}
              className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-accent-orange/40 rounded-full text-white transition-colors"
              aria-label="Fechar"
              data-testid="eneaflix-trailer-close"
            >
              <iconify-icon icon="solar:close-circle-linear" className="text-3xl"></iconify-icon>
            </button>
            <div
              className="w-full max-w-5xl aspect-video bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={`${embed}?autoplay=1`}
                title={item.title}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
        )}

        {/* Related — dark theme */}
        {related.length > 0 && (
          <section
            className="relative z-10 py-20 border-t border-neutral-900"
            style={{ background: 'rgba(17, 17, 17, 0.85)' }}
          >
            <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
                Para o mesmo perfil
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-[#EAEAEA] mb-2">
                Outras indicações para {item.enneagram_type}
              </h3>
              <p className="text-sm text-neutral-500 mb-10">
                Selecionados pelo padrão comportamental dominante.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {related.map((r) => (
                  <Link
                    to={`/eneaflix/${r.eneaflix_id}`}
                    key={r.eneaflix_id}
                    className="group block rounded-xl overflow-hidden bg-[#1A1A1A] ring-1 ring-white/5 hover:ring-accent-orange transition-all duration-300 hover:-translate-y-1"
                    data-testid={`related-eneaflix-${r.eneaflix_id}`}
                  >
                    <div className="relative aspect-[2/3] bg-[#0A0A0A] overflow-hidden">
                      {r.image ? (
                        <img
                          src={r.image}
                          alt={r.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center bg-gradient-to-br from-[#1F1F1F] to-[#000000]">
                          <iconify-icon icon="solar:gallery-linear" className="text-3xl text-neutral-700"></iconify-icon>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent"></div>
                      <div className="absolute inset-x-0 bottom-0 p-3">
                        <h4 className="font-semibold text-xs md:text-sm text-white line-clamp-2 leading-tight drop-shadow">
                          {r.title}
                        </h4>
                        <p className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">
                          {r.type}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default EneaflixDetail;
